import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';

import * as Styled from './StandaloneToolCard.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';
import { highlightText } from '../utils/highlightText';

/**
 * A card component for displaying standalone tools with a background image, icon, and CTA
 * @param {object} props - Component props
 * @param {string} props.title - The main title of the card
 * @param {string} props.subtitle - The subtitle displayed above the title
 * @param {string} props.thumbnail - The URL of the background image for the top half
 * @param {string} [props.icon] - The URL of the icon displayed over the image
 * @param {string} props.ctaText - The text of the CTA button
 * @param {string} props.ctaUrl - The URL the CTA button links to
 * @component
 */
function StandaloneToolCard({ title, subtitle, thumbnail, icon, ctaText, ctaUrl }) {
  return (
    <Styled.StandaloneToolCard>
      <Box sx={{ position: 'relative' }}>
        <Styled.ImageContainer>
          <Styled.Thumbnail alt={title || 'tool background'} src={thumbnail} />
        </Styled.ImageContainer>
        {icon && <Styled.Icon alt={`${title} icon`} src={icon} />}
        <Styled.Content>
          <Stack gap={0.5}>
            <Styled.Title size="xs">{title}</Styled.Title>
            <Styled.Subtitle fontWeight="600" size="md">
              {subtitle}
            </Styled.Subtitle>
          </Stack>
          <Styled.CtaButton
            clickstreamProps={{
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: 'velo_standalone_tool_card',
              elementText: ctaText
            }}
            color="primary"
            fullWidth
            href={ctaUrl}
            size="medium"
            target="_blank"
            variant="contained"
          >
            {highlightText(ctaText, Styled.TextHighlight)}
            <Styled.ArrowForwardIcon />
          </Styled.CtaButton>
        </Styled.Content>
      </Box>
    </Styled.StandaloneToolCard>
  );
}

StandaloneToolCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  thumbnail: PropTypes.string.isRequired,
  icon: PropTypes.string,
  ctaText: PropTypes.string.isRequired,
  ctaUrl: PropTypes.string.isRequired
};

StandaloneToolCard.defaultProps = {
  icon: null
};

export default StandaloneToolCard;
